import { useEffect, useMemo, useState } from "react";
import { Card, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useQuery } from "@tanstack/react-query";
import { listarCategoriasDocumento } from "@/features/admin-categorias-documento/services/categorias-documento-service";
import type { TipoDocumentoAdmin } from "../services/tipos-documento-service";
import { useTiposDocumentoAdmin } from "../hooks/use-tipos-documento-admin";
import { listarTiposDocumentoAdmin } from "../services/tipos-documento-service";
import { TiposDocumentoHeader } from "./TiposDocumentoHeader";
import { TipoDocumentoCrearForm } from "./TipoDocumentoCrearForm";
import { TiposDocumentoTable } from "./TiposDocumentoTable";

const PAGE_SIZE = 15;

/**
 * Página de administración del catálogo de tipos de documento (P31 · Gestión Documental fase 2:
 * cada tipo pertenece a una categoría y puede sobrescribir su configuración documental).
 * La búsqueda y el paginado se resuelven en el SERVIDOR (query params con
 * debounce de 350ms) · el loader del route precarga la primera página.
 */
export function AdminTiposDocumentoPage() {
  const [modoCrear, setModoCrear] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("");
  // Categorías que admiten tipos (activas y que no registra el servidor).
  const { data: catalogo } = useQuery({
    queryKey: ["admin", "categorias-documento", "para-tipos"],
    queryFn: () => listarCategoriasDocumento({ soloActivas: true, pageSize: 100 }),
  });
  const categorias = (catalogo?.items ?? []).filter((c) => !c.esDelServidor).map((c) => ({ codigo: c.codigo, nombre: c.nombre }));
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const filtrosServer = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      ...(categoria ? { categoria } : {}),
    }),
    [busquedaDebounced, categoria]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced, categoria]);

  const { data } = useTiposDocumentoAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const tipos = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodos = async (): Promise<TipoDocumentoAdmin[]> => {
    const todos: TipoDocumentoAdmin[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarTiposDocumentoAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <TiposDocumentoHeader
        total={data?.totalCatalogo}
        activos={data?.totalActivos}
        modoCrear={modoCrear}
        onIniciarCrear={() => setModoCrear(true)}
      />
      <Stack gap="md">
        {modoCrear ? <TipoDocumentoCrearForm categorias={categorias} categoriaInicial={categoria || undefined} onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <TiposDocumentoTable
              cargando={!data}
              tipos={tipos}
              busqueda={busqueda}
              onBusquedaChange={setBusqueda}
              categorias={categorias}
              categoria={categoria}
              onCategoriaChange={setCategoria}
              sinResultados={total === 0}
              hayBusqueda={busquedaDebounced.trim().length > 0}
              obtenerFilas={obtenerTodos}
            />
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="tipos" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
